import EmptyState from '@components/Common/EmptyState';
import { MetricColumn } from '@components/Common/MetricTable';
import { Panel } from '@components/Common/Panel';
import SkeletonLoader from '@components/Common/Skeleton/SkeletonLoader';
import CurrencyRupeeOutlinedIcon from '@mui/icons-material/CurrencyRupeeOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Button, Chip, LinearProgress } from '@mui/material';
import ActionIconButton from '@components/Common/ActionIconButton';
import FilterRow from '@components/Common/FilterRow';
import axios from 'axios';
import React, { useCallback, useEffect, useState } from 'react';
import {
  budgetTone,
  CostSummary,
  forecastMethodLabel,
  forecastPct,
  inr,
  monthLabel,
  ProviderId,
  ProviderSummary,
  stateLabel,
  usd,
  usdWhole,
} from './costData';
import { Amount, CostSection, CostTiles } from './CostParts';

/*
 * All costs: every provider's month on one screen, and the total across them.
 *
 * A provider the server cannot read stays on the list with its reason and is left out of the
 * total, which then says how many providers it covers. It is never drawn as $0.
 */

const TAB_FOR: Partial<Record<ProviderId, string>> = { aws: 'aws', openai: 'openai', mongodb: 'mongodb', google: 'google' };

const Unknown: React.FC<{ row: ProviderSummary }> = ({ row }) => <span className="ac-unknown">{stateLabel(row.state)}</span>;

const money = (row: ProviderSummary, cents: number | null) =>
  row.state !== 'ok' ? <Unknown row={row} /> : cents == null ? <span className="ac-unknown">Not available</span> : <Amount cents={cents} />;

const LimitBar: React.FC<{ row: ProviderSummary }> = ({ row }) => {
  if (row.state !== 'ok') return <Unknown row={row} />;
  const pct = forecastPct(row.forecastCents, row.limitCents);
  if (pct == null) return <span className="ac-unknown">No limit set</span>;
  const tone = budgetTone(pct);
  return (
    <span className="ac-limit">
      <LinearProgress
        variant="determinate"
        value={Math.min(100, pct)}
        className={`ac-limit-bar ac-limit-bar--${tone}`}
        aria-label={`${row.name} forecast is ${pct}% of the monthly limit`}
      />
      <span className="ac-limit-label">
        {pct}% of {usdWhole(row.limitCents)}
      </span>
    </span>
  );
};

const STATE_CHIP: Record<ProviderSummary['state'], 'success' | 'warning' | 'error'> = {
  ok: 'success',
  not_configured: 'warning',
  access_denied: 'warning',
  error: 'error',
};

function columns(onOpen: (id: ProviderId) => void): MetricColumn<ProviderSummary>[] {
  return [
    {
      key: 'name',
      header: 'Provider',
      variant: 'name',
      render: (r) =>
        TAB_FOR[r.id] ? (
          <button type="button" className="ac-provider-link" onClick={() => onOpen(r.id)}>
            {r.name}
          </button>
        ) : (
          r.name
        ),
    },
    {
      key: 'state',
      header: 'Status',
      align: 'left',
      render: (r) => <Chip size="small" color={STATE_CHIP[r.state]} label={stateLabel(r.state)} />,
    },
    {
      key: 'mtd',
      header: 'Month to date',
      align: 'right',
      render: (r) => money(r, r.monthToDateCents),
      sortValue: (r) => r.monthToDateCents ?? -1,
    },
    {
      key: 'last',
      header: 'Last month',
      align: 'right',
      render: (r) => money(r, r.lastMonthCents),
      sortValue: (r) => r.lastMonthCents ?? -1,
    },
    {
      key: 'forecast',
      header: 'Forecast',
      tooltip: 'Month-end estimate. AWS uses its own forecast; the others carry the daily rate so far forward.',
      align: 'right',
      render: (r) => money(r, r.forecastCents),
      sortValue: (r) => r.forecastCents ?? -1,
    },
    { key: 'method', header: 'Forecast basis', align: 'left', render: (r) => (r.state === 'ok' ? forecastMethodLabel(r.forecastMethod) : '-') },
    { key: 'limit', header: 'Forecast vs limit', align: 'left', render: (r) => <LimitBar row={r} /> },
  ];
}

const AllCostsPanel: React.FC<{ onOpenProvider: (tab: string) => void }> = ({ onOpenProvider }) => {
  const [data, setData] = useState<CostSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setFailed(false);
    try {
      const res = await axios.get('/api/cost-summary');
      setData(res.data);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toolbar = (
    <FilterRow className="ac-toolbar">
      <ActionIconButton label="Refresh" icon={<RefreshIcon />} busy={loading} onClick={load} />
    </FilterRow>
  );

  if (loading && !data) return <>{toolbar}<SkeletonLoader rows={6} columns={5} /></>;
  if (failed || !data) {
    return (
      <>
        {toolbar}
        <Panel>
          <EmptyState
            title="Costs could not be loaded"
            body="The cost summary did not answer. Try again in a moment."
            actions={<Button variant="outlined" onClick={load}>Try again</Button>}
          />
        </Panel>
      </>
    );
  }

  const { totals } = data;
  const coverage = totals.complete ? 'All providers' : `${totals.connected} of ${totals.of} providers`;
  const problems = data.providers.filter((p) => p.state !== 'ok');
  const notes = data.providers.filter((p) => p.state === 'ok' && p.note);

  return (
    <>
      {toolbar}
      <CostTiles
        tiles={[
          { label: 'Month to date', value: usd(totals.monthToDateCents), sublabel: `${monthLabel(data.month)}, ${coverage.toLowerCase()}`, icon: <PaymentsOutlinedIcon /> },
          { label: 'Last month', value: usd(totals.lastMonthCents), sublabel: monthLabel(data.lastMonth), icon: <HistoryOutlinedIcon /> },
          { label: 'Forecast', value: usd(totals.forecastCents), sublabel: 'Month end, connected providers', icon: <TrendingUpOutlinedIcon /> },
          {
            label: 'Month to date in INR',
            value: inr(data.inr.monthToDate),
            sublabel: `Approximate, at ₹${data.inr.rate} to the dollar${data.inr.isDefault ? ' (default rate)' : ''}`,
            icon: <CurrencyRupeeOutlinedIcon />,
          },
        ]}
      />
      <CostSection<ProviderSummary>
        title="By provider"
        icon={<TableRowsOutlinedIcon />}
        meta={coverage}
        columns={columns((id) => TAB_FOR[id] && onOpenProvider(TAB_FOR[id]!))}
        rows={data.providers}
        getRowKey={(r) => r.id}
        footnote={
          <>
            {!totals.complete && (
              <>
                Totals cover {totals.connected} of {totals.of} providers. A provider that is not connected is left
                out, never counted as $0.{' '}
              </>
            )}
            Last month in INR {inr(data.inr.lastMonth)}, forecast {inr(data.inr.forecast)}.
            {problems.map((p) => (
              <span key={p.id} className="ac-footnote-line">
                {p.name}: {p.message}
              </span>
            ))}
            {notes.map((p) => (
              <span key={p.id} className="ac-footnote-line">
                {p.name}: {p.note}
              </span>
            ))}
          </>
        }
      />
      {data.fixedItems.length > 0 && (
        <CostSection<{ name: string; usd: number }>
          title="Fixed monthly costs"
          icon={<ReceiptLongOutlinedIcon />}
          meta={`${data.fixedItems.length} item${data.fixedItems.length === 1 ? '' : 's'}`}
          columns={[
            { key: 'name', header: 'Item', variant: 'name', render: (r) => r.name },
            { key: 'usd', header: 'Per month', align: 'right', render: (r) => <Amount cents={Math.round(r.usd * 100)} /> },
          ]}
          rows={data.fixedItems}
          getRowKey={(r, i) => `${r.name}-${i}`}
          footnote="From FIXED_MONTHLY_COSTS_JSON on the server. Counted in full in every month."
        />
      )}
      <p className="ac-footnote">
        Months are calendar months in UTC, as the providers bill
        them. AWS figures refresh every six hours, since each Cost Explorer request is billed.
      </p>
    </>
  );
};

export default AllCostsPanel;
